import type { FieldConfig, FieldValue, FieldValues } from '@shared/types/interactiveFields';
import { splitTextByPlaceholders } from '../../../utils/placeholders';
import { InlineFieldRenderer } from './InlineFieldRenderer';

type Language = 'bg' | 'de' | 'en';

interface DeclarationTextWithFieldsProps {
  /** Declaration text with placeholders */
  text: string;
  /** Available fields schema */
  fields: FieldConfig[];
  /** Current field values */
  values: FieldValues;
  /** Callback when field value changes */
  onChange: (values: FieldValues) => void;
  /** Field validation errors */
  errors?: Record<string, string>;
  /** Disable all fields */
  disabled?: boolean;
  /** Language for labels */
  language: Language;
}

/**
 * Renders declaration text with inline interactive fields
 * Replaces {{field:fieldId}} placeholders with actual input fields
 */
export function DeclarationTextWithFields({
  text,
  fields,
  values,
  onChange,
  errors = {},
  disabled = false,
  language,
}: DeclarationTextWithFieldsProps) {
  // Split text into segments (text and placeholders)
  const segments = splitTextByPlaceholders(text);

  // Create a map of fields by ID for quick lookup
  const fieldsById = fields.reduce((acc, field) => {
    acc[field.id] = field;
    return acc;
  }, {} as Record<string, FieldConfig>);

  // Handle field value change
  const handleFieldChange = (fieldId: string, value: FieldValue) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          return <span key={index}>{segment.content}</span>;
        }

        // Placeholder segment
        const fieldId = segment.fieldId!;
        const field = fieldsById[fieldId];

        if (!field) {
          // Field not found - show placeholder as-is
          return (
            <span
              key={index}
              className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs"
              title={`Полето "${fieldId}" не е намерено`}
            >
              {segment.content}
            </span>
          );
        }

        // Render inline field
        return (
          <InlineFieldRenderer
            key={index}
            field={field}
            value={values[fieldId]}
            onChange={(value) => handleFieldChange(fieldId, value)}
            error={errors[fieldId]}
            disabled={disabled}
            language={language}
          />
        );
      })}
    </div>
  );
}
